import dayjs from "dayjs";
import type { APIListing } from "models/types/api-listing";
import { AD_PLATFORMS } from "modules/vendor-portal/screens/performance/utils/ad-platform-details";

/**
 * Aggregates a specific statistic across all portal ad campaigns for a listing
 * within a specified number of past days.
 *
 * @param listing The listing data containing performance stats.
 * @param statType The key of the statistic to aggregate (e.g., "clicks_key").
 * @param pastXDays The number of days in the past to include. If undefined, all values will be aggregated.
 * @returns The total aggregated value for the specified stat.
 */
export function getAggregatedPortalCampaignStat(
  listing: APIListing | undefined,
  statType: "clicks_key", // Currently only supporting clicks
  pastXDays?: number,
): number {
  const campaigns = listing?.performance?.stats;
  if (!campaigns) {
    return 0;
  }

  let cutoffDate: dayjs.Dayjs | undefined;
  if (typeof pastXDays === "number" && pastXDays >= 0) {
    cutoffDate = dayjs().subtract(pastXDays, "day");
  }

  return campaigns.reduce((totalStatValue, campaignStat) => {
    const formatId = campaignStat?.format?.id;
    // Ensure formatId is a valid key for AD_PLATFORMS
    if (!formatId || (!(formatId in AD_PLATFORMS) && formatId !== "reach")) {
      return totalStatValue;
    }

    // Ensure campaignStat.value is an object
    if (typeof campaignStat.value !== "object" || campaignStat.value === null) {
      return totalStatValue;
    }

    let campaignTotal = 0;

    // Add reach summary clicks if accross total campaign
    if (formatId === "reach" && !cutoffDate) {
      const clicksValue = (
        campaignStat.value as { summary?: { clicks?: number } }
      ).summary?.clicks;
      if (typeof clicksValue === "number") {
        campaignTotal = clicksValue;
      }
    } else {
      const platformConfig =
        AD_PLATFORMS[formatId as keyof typeof AD_PLATFORMS];
      const statKey = platformConfig?.[statType];

      if (statKey) {
        campaignTotal = Object.entries(campaignStat.value).reduce(
          (dailyTotal, [date, dailyStat]) => {
            const isDateInRange =
              !cutoffDate || dayjs(date).isAfter(cutoffDate);

            if (
              isDateInRange &&
              typeof dailyStat === "object" &&
              dailyStat !== null &&
              statKey in dailyStat
            ) {
              const statValue = (dailyStat as Record<string, unknown>)[statKey];
              if (typeof statValue === "number") {
                return dailyTotal + statValue;
              }
            }
            return dailyTotal;
          },
          0,
        );
      }
    }

    return totalStatValue + campaignTotal;
  }, 0);
}
